import React from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import SearchIcon from "@mui/icons-material/Search";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useFundamentalsSelection } from "./FundamentalsSelectionContext";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import ArrowCircleDownIcon from "@mui/icons-material/ArrowCircleDown";
import HealingIcon from "@mui/icons-material/Healing";
import BloodtypeIcon from "@mui/icons-material/Bloodtype";
import OfflineBoltIcon from "@mui/icons-material/OfflineBolt";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import PaymentsIcon from "@mui/icons-material/Payments";
import FactoryIcon from "@mui/icons-material/Factory";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";

function CustomBreadcrumbs() {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	const breadcrumbs = [
		<Link
			component={RouterLink}
			variant="body2"
			underline="hover"
			key="1"
			color="inherit"
			to="/"
		>
			Home
		</Link>,
		<Typography
			key="3"
			variant="body2"
			sx={{ color: "text.primary", marginTop: 0.1 }}
		>
			Fundamentals Search
		</Typography>,
	];

	return (
		<Breadcrumbs
			separator="›"
			aria-label="breadcrumb"
			sx={{
				marginTop: isMobile
					? `calc(61px + 0.3rem)`
					: `calc(65px + 0.3rem)`,
				marginLeft: "0.3rem",
				alignContent: "center",
			}}
		>
			{breadcrumbs}
		</Breadcrumbs>
	);
}

function FundamentalsSearchButton({ text, onClick, selected }) {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const isTablet = useMediaQuery(theme.breakpoints.down("md"));
	const isBigTablet = useMediaQuery(theme.breakpoints.down("lg"));

	return (
		<Grid>
			<Button
				size="large"
				variant={selected ? "contained" : "outlined"}
				endIcon={selected ? <RemoveIcon /> : <AddIcon />}
				onClick={onClick}
				sx={{
					textTransform: "none",
					borderRadius: "1.25rem",
					width: isMobile
						? "70vw"
						: isTablet
						? "45vw"
						: isBigTablet
						? "30vw"
						: "22.5vw",
					justifyContent: "space-between",
					// border: '1px dashed grey'
					backgroundColor: selected ? "success.main" : "",
				}}
			>
				{text}
			</Button>
		</Grid>
	);
}

function PopularSearchButton({ text, icon }) {
	return (
		<Grid>
			<Button
				size="large"
				variant="outlined"
				startIcon={icon}
				sx={{
					textTransform: "none",
					borderRadius: "1.25rem",
					// border: '1px dashed grey'
				}}
			>
				{text}
			</Button>
		</Grid>
	);
}

function FundamentalsSearchButtonGroup() {
	const theme = useTheme();
	const isBigScreen = useMediaQuery(theme.breakpoints.down("lg"));
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const navigate = useNavigate(); // Hook for programmatic navigation

	const goToResults = () => {
		navigate("/fundamentals-search/results");
	};

	// State to track the selected status of each button
	const { selectedButtons, toggleButtonSelection } =
		useFundamentalsSelection();

	// Calculate if any button is selected
	const isAnyButtonSelected = Object.values(selectedButtons).some(
		(selected) => selected
	);

	return (
		<Box
			sx={{
				marginTop: isBigScreen ? "2rem" : "3rem",
				marginLeft: isBigScreen ? "auto" : "2.5vw",
				marginRight: isBigScreen ? "auto" : "",
				maxWidth: isBigScreen ? "88vw" : "47.5vw + 3rem",
				// border: '1px dashed grey',
				paddingRight: isBigScreen ? "" : "3rem",
				borderRight: isBigScreen ? "" : "1px solid lightgrey",
				paddingBottom: isBigScreen ? "2rem" : "",
				borderBottom: isBigScreen ? "1px solid lightgrey" : "",
			}}
		>
			<Typography
				component="h2"
				variant="h6"
				align="center"
				fontWeight="bold"
				sx={{}}
			>
				Fundamentals
			</Typography>
			<Grid
				container
				gap={isMobile ? 1 : 2}
				justifyContent={isBigScreen ? "center" : "space-between"}
				sx={{ marginTop: isMobile ? "1rem" : "1.5rem", width: "100%" }}
			>
				<FundamentalsSearchButton
					text="5YR P/E Ratio < 22.5"
					onClick={() => toggleButtonSelection("peRatio")}
					selected={selectedButtons.peRatio}
				></FundamentalsSearchButton>
				<FundamentalsSearchButton
					text="5YR ROIC"
					onClick={() => toggleButtonSelection("roic")}
					selected={selectedButtons.roic}
				></FundamentalsSearchButton>
				<FundamentalsSearchButton
					text="Revenue Growth 5 Yr"
					onClick={() => toggleButtonSelection("revenueGrowth")}
					selected={selectedButtons.revenueGrowth}
				></FundamentalsSearchButton>
				<FundamentalsSearchButton
					text="Net Income Growth 5 Yr"
					onClick={() => toggleButtonSelection("netIncomeGrowth")}
					selected={selectedButtons.netIncomeGrowth}
				></FundamentalsSearchButton>
				<FundamentalsSearchButton
					text="Shares Outstanding"
					onClick={() => toggleButtonSelection("sharesOutstanding")}
					selected={selectedButtons.sharesOutstanding}
				></FundamentalsSearchButton>
				<FundamentalsSearchButton
					text="LTL / 5 Yr FCF < 5"
					onClick={() => toggleButtonSelection("ltlToFcf")}
					selected={selectedButtons.ltlToFcf}
				></FundamentalsSearchButton>
				<FundamentalsSearchButton
					text="Cash Flow Growth 5 Yr"
					onClick={() => toggleButtonSelection("cashFlowGrowth")}
					selected={selectedButtons.cashFlowGrowth}
				></FundamentalsSearchButton>
				<FundamentalsSearchButton
					text="5YR Price:FCF < 22.5"
					onClick={() => toggleButtonSelection("priceToFcf")}
					selected={selectedButtons.priceToFcf}
				></FundamentalsSearchButton>
			</Grid>
			<Box
				display="flex"
				justifyContent="center"
				sx={{ marginTop: "1.3rem" }}
			>
				<Button
					startIcon={<SearchIcon />}
					variant="contained"
					onClick={goToResults}
					disabled={!isAnyButtonSelected} // Disable if no button is selected
					sx={{ textTransform: "none", borderRadius: 3 }}
				>
					Search
				</Button>
			</Box>
		</Box>
	);
}

function PopularSearchButtonGroup() {
	const theme = useTheme();
	const isBigScreen = useMediaQuery(theme.breakpoints.down("lg"));
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	return (
		<Box
			sx={{
				marginTop: isBigScreen ? "1.5rem" : "3rem",
				marginLeft: isBigScreen ? "auto" : "3rem",
				marginRight: isBigScreen ? "auto" : "2.5vw",
				maxWidth: isBigScreen ? "88vw" : "37.5vw",
				marginBottom: "5rem",
				// border: '1px dashed grey',
			}}
		>
			<Typography
				component="h2"
				variant="h6"
				align="center"
				fontWeight={"bold"}
				sx={{}}
			>
				Popular Searches
			</Typography>
			<Grid
				container
				gap={isMobile ? 1 : 2}
				justifyContent={isBigScreen ? "center" : "space-between"}
				sx={{ marginTop: isMobile ? "1rem" : "1.5rem", width: "100%" }}
			>
				<PopularSearchButton
					text="Biggest Gainers"
					icon={<EmojiEventsIcon />}
				></PopularSearchButton>
				<PopularSearchButton
					text="Biggers Losers"
					icon={<ArrowCircleDownIcon />}
				></PopularSearchButton>
				<PopularSearchButton
					text="Utilities"
					icon={<BloodtypeIcon />}
				></PopularSearchButton>

				<PopularSearchButton
					text="Healthcare"
					icon={<HealingIcon />}
				></PopularSearchButton>
				<PopularSearchButton
					text="Energy"
					icon={<OfflineBoltIcon />}
				></PopularSearchButton>
				<PopularSearchButton
					text="Real State"
					icon={<HomeWorkIcon />}
				></PopularSearchButton>
				<PopularSearchButton
					text="Financials"
					icon={<PaymentsIcon />}
				></PopularSearchButton>
				<PopularSearchButton
					text="Industrials"
					icon={<FactoryIcon />}
				></PopularSearchButton>
				<PopularSearchButton
					text="52 Week High"
					icon={<TrendingUpIcon />}
				></PopularSearchButton>
				<PopularSearchButton
					text="52 Week Low"
					icon={<TrendingDownIcon />}
				></PopularSearchButton>
				<PopularSearchButton
					text="Near 52 Week Low"
					icon={<ThumbDownAltIcon />}
				></PopularSearchButton>
			</Grid>
		</Box>
	);
}

export default function FundamentalsSearch() {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

	return (
		<>
			<CustomBreadcrumbs></CustomBreadcrumbs>
			<Box display={isMobile ? "" : "flex"} sx={{}}>
				<FundamentalsSearchButtonGroup></FundamentalsSearchButtonGroup>
				<PopularSearchButtonGroup></PopularSearchButtonGroup>
			</Box>
		</>
	);
}
